/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {combineEpics, Epic} from 'redux-observable';
import {filter, map} from 'rxjs/operators';
import {notificationAdded} from '@pompkracht/react-notifications/dist/actions';
import {v4 as uuidv4} from 'uuid';
import {isActionOf} from 'typesafe-actions';
import {
	iamApiAccountRecoveryEnterPasswordNotFound,
	iamApiAccountRecoveryEnterPasswordUnexpectedError,
	iamApiAccountRecoveryEnterPhoneVerificationCodeNotFound,
	iamApiAccountRecoveryEnterPhoneVerificationCodeUnexpectedError,
	iamApiAccountRecoveryEvaluatePasswordStrengthNotFound,
	iamApiAccountRecoveryEvaluatePasswordStrengthUnexpectedError,
	iamApiAccountRecoveryRestartPhoneVerificationNotFound,
	iamApiAccountRecoveryRestartPhoneVerificationUnexpectedError,
	iamApiAccountRecoveryRetryPhoneVerificationNotFound,
	iamApiAccountRecoveryRetryPhoneVerificationUnexpectedError,
	iamApiAuthenticationChangePasswordNotFound,
	iamApiAuthenticationChangePasswordUnexpectedError,
	iamApiAuthenticationEnterPhoneVerificationCodeNotFound,
	iamApiAuthenticationEnterPhoneVerificationCodeUnexpectedError,
	iamApiAuthenticationRestartPhoneVerificationNotFound,
	iamApiAuthenticationRestartPhoneVerificationUnexpectedError,
	iamApiAuthenticationRetryPhoneVerificationNotFound,
	iamApiAuthenticationRetryPhoneVerificationUnexpectedError,
	iamApiEmployeeRegistrationEnterPasswordNotFound,
	iamApiEmployeeRegistrationEnterPasswordUnexpectedError,
	iamApiEmployeeRegistrationEnterPhoneVerificationCodeNotFound,
	iamApiEmployeeRegistrationEnterPhoneVerificationCodeUnexpectedError,
	iamApiEmployeeRegistrationEvaluatePasswordStrengthNotFound,
	iamApiEmployeeRegistrationEvaluatePasswordStrengthUnexpectedError,
	iamApiEmployeeRegistrationRestartPhoneVerificationNotFound,
	iamApiEmployeeRegistrationRestartPhoneVerificationUnexpectedError,
	iamApiEmployeeRegistrationRetryPhoneVerificationNotFound,
	iamApiEmployeeRegistrationRetryPhoneVerificationUnexpectedError,
	iamApiEvaluatePasswordStrengthUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPasswordNotFound,
	iamApiFlexServiceWorkerRegistrationEnterPasswordUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberNotFound,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeNotFound,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthNotFound,
	iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthUnexpectedError,
	iamApiFlexServiceWorkerRegistrationResolvePersonalNameNotFound,
	iamApiFlexServiceWorkerRegistrationResolvePersonalNameUnexpectedError,
	iamApiFlexServiceWorkerRegistrationStopPhoneVerificationNotFound,
	iamApiFlexServiceWorkerRegistrationStopPhoneVerificationUnexpectedError,
	iamApiPerformPhoneNumberLookupUnexpectedError,
	iamApiRetrieveAccountEmailChangeStateNotFound,
	iamApiRetrieveAccountEmailChangeStateUnexpectedError,
	iamApiRetrieveAccountRecoveryStateNotFound,
	iamApiRetrieveAccountRecoveryStateUnexpectedError,
	iamApiRetrieveEmployeeRegistrationStateNotFound,
	iamApiRetrieveEmployeeRegistrationStateUnexpectedError,
	iamApiRetrieveFlexServiceWorkerRegistrationStateNotFound,
	iamApiRetrieveFlexServiceWorkerRegistrationStateUnexpectedError,
	iamApiStartAccountRecoveryUnexpectedError,
	iamApiStartAuthenticationUnexpectedError,
	iamApiStartRememberMeAuthenticationUnexpectedError
} from '../services/IamApi/actions';

const showNotificationOnSpecifiedApiUnexpectedErrors: Epic = (action$) => action$.pipe(
	filter(isActionOf([
		iamApiRetrieveAccountEmailChangeStateUnexpectedError,
		iamApiStartAccountRecoveryUnexpectedError,
		iamApiRetrieveAccountRecoveryStateUnexpectedError,
		iamApiAccountRecoveryRetryPhoneVerificationUnexpectedError,
		iamApiAccountRecoveryRestartPhoneVerificationUnexpectedError,
		iamApiAccountRecoveryEnterPhoneVerificationCodeUnexpectedError,
		iamApiAccountRecoveryEvaluatePasswordStrengthUnexpectedError,
		iamApiAccountRecoveryEnterPasswordUnexpectedError,
		iamApiRetrieveEmployeeRegistrationStateUnexpectedError,
		iamApiEmployeeRegistrationRetryPhoneVerificationUnexpectedError,
		iamApiEmployeeRegistrationRestartPhoneVerificationUnexpectedError,
		iamApiEmployeeRegistrationEnterPhoneVerificationCodeUnexpectedError,
		iamApiEmployeeRegistrationEvaluatePasswordStrengthUnexpectedError,
		iamApiEmployeeRegistrationEnterPasswordUnexpectedError,
		iamApiRetrieveFlexServiceWorkerRegistrationStateUnexpectedError,
		iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError,
		iamApiFlexServiceWorkerRegistrationStopPhoneVerificationUnexpectedError,
		iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeUnexpectedError,
		iamApiFlexServiceWorkerRegistrationResolvePersonalNameUnexpectedError,
		iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthUnexpectedError,
		iamApiFlexServiceWorkerRegistrationEnterPasswordUnexpectedError,
		iamApiEvaluatePasswordStrengthUnexpectedError,
		iamApiPerformPhoneNumberLookupUnexpectedError,
		iamApiStartRememberMeAuthenticationUnexpectedError,
		iamApiStartAuthenticationUnexpectedError,
		iamApiAuthenticationRetryPhoneVerificationUnexpectedError,
		iamApiAuthenticationRestartPhoneVerificationUnexpectedError,
		iamApiAuthenticationEnterPhoneVerificationCodeUnexpectedError,
		iamApiAuthenticationChangePasswordUnexpectedError
	])),
	map(() => notificationAdded({
			notification:
				{
					identifier: uuidv4(),
					level: 'danger',
					text: 'Er is iets misgegaan. U kunt proberen uw laatste actie nog eens te herhalen.'
				},
			autoClose: true
		}
	))
);

const showNotificationOnSpecifiedApiNotFoundErrors: Epic = (action$) => action$.pipe(
	filter(isActionOf([
		iamApiRetrieveAccountEmailChangeStateNotFound,
		iamApiRetrieveAccountRecoveryStateNotFound,
		iamApiAccountRecoveryRetryPhoneVerificationNotFound,
		iamApiAccountRecoveryRestartPhoneVerificationNotFound,
		iamApiAccountRecoveryEnterPhoneVerificationCodeNotFound,
		iamApiAccountRecoveryEvaluatePasswordStrengthNotFound,
		iamApiAccountRecoveryEnterPasswordNotFound,
		iamApiRetrieveEmployeeRegistrationStateNotFound,
		iamApiEmployeeRegistrationRetryPhoneVerificationNotFound,
		iamApiEmployeeRegistrationRestartPhoneVerificationNotFound,
		iamApiEmployeeRegistrationEnterPhoneVerificationCodeNotFound,
		iamApiEmployeeRegistrationEvaluatePasswordStrengthNotFound,
		iamApiEmployeeRegistrationEnterPasswordNotFound,
		iamApiRetrieveFlexServiceWorkerRegistrationStateNotFound,
		iamApiFlexServiceWorkerRegistrationEnterPhoneNumberNotFound,
		iamApiFlexServiceWorkerRegistrationStopPhoneVerificationNotFound,
		iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeNotFound,
		iamApiFlexServiceWorkerRegistrationResolvePersonalNameNotFound,
		iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthNotFound,
		iamApiFlexServiceWorkerRegistrationEnterPasswordNotFound,
		iamApiAuthenticationRetryPhoneVerificationNotFound,
		iamApiAuthenticationRestartPhoneVerificationNotFound,
		iamApiAuthenticationEnterPhoneVerificationCodeNotFound,
		iamApiAuthenticationChangePasswordNotFound
	])),
	map(() => notificationAdded({
			notification: {
				identifier: uuidv4(),
				level: 'danger',
				text: 'Er is iets misgegaan. We hebben niet kunnen vinden wat u zocht.'
			},
			autoClose: true
		}
	))
);

const notificationEpics: Epic = combineEpics(
	showNotificationOnSpecifiedApiUnexpectedErrors,
	showNotificationOnSpecifiedApiNotFoundErrors
);

export default notificationEpics;
